import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { CommentViewModel, CommentResolutionStatus } from '../models/comment.model';
import { CommentViewModel as BackendCommentViewModel, backendToFrontendComment, autogeneratedToFrontendComment
} from '../models/comment-api.models';
import {
  EsraPipingAPIClient,
  SaveCommentsRequest,
  UpdateCommentResolutionStatusRequest,
  UpdateCommentRequest as AutogenUpdateCommentRequest,
  AddReplyRequest,
  CreateCommentRequest as AutogenCreateCommentRequest,
  CommentResolutionStatus as AutogenCommentResolutionStatus,
  CommentViewModel as AutogeneratedCommentViewModel
} from '../models/autogenerated-piping';

export interface CreateCommentRequest {
  pipeClassId: string;
  revisionId: string;
  contextType: 'summary' | 'components' | 'valves' | 'branchtables';
  parentCommentId?: string;
  content: string;
  authorName?: string;
}

export interface UpdateCommentRequest {
  id: string;
  content: string;
  resolutionStatus?: CommentResolutionStatus;
}

export interface DeleteCommentRequest {
  id: string;
}

export interface CommentsResponse {
  comments: CommentViewModel[];
  totalCount: number;
}

export interface SaveAllCommentsRequest {
  pipeClassId: string;
  revisionId: string;
  summaryComments: CommentViewModel[];
  componentComments: CommentViewModel[];
  valveComments: CommentViewModel[];
  branchTableComments: CommentViewModel[];
}

@Injectable({
  providedIn: 'root'
})
export class CommentsService {

  private readonly baseUrl = '/api/Comments';

  constructor(
    private http: HttpClient,
    private esraApiClient: EsraPipingAPIClient
  ) { }

  /**
   * Gets all comments for a specific context (summary, components, valves, branchtables).
   * The backend now returns a nested structure (replies embedded in each root comment).
   */
  getComments(pipeClassId: string, revisionId: string, contextType: 'summary' | 'components' | 'valves' | 'branchtables'): Observable<CommentsResponse> {
    return this.esraApiClient.getComments(pipeClassId, parseInt(revisionId), contextType)
      .pipe(
        map((backendComments: AutogeneratedCommentViewModel[]) => {
          // autogeneratedToFrontendComment now handles nested replies recursively.
          const threadedComments = backendComments.map(c => autogeneratedToFrontendComment(c));
          return {
            comments: threadedComments,
            totalCount: threadedComments.length
          };
        })
      );
  }

  /**
   * Gets comments for all contexts for a pipe class and revision
   */
  getAllComments(pipeClassId: string, revisionId: string): Observable<{
    summary: CommentViewModel[];
    components: CommentViewModel[];
    valves: CommentViewModel[];
    branchtables: CommentViewModel[];
  }> {
    return forkJoin({
      summary: this.getComments(pipeClassId, revisionId, 'summary').pipe(map(r => r.comments)),
      components: this.getComments(pipeClassId, revisionId, 'components').pipe(map(r => r.comments)),
      valves: this.getComments(pipeClassId, revisionId, 'valves').pipe(map(r => r.comments)),
      branchtables: this.getComments(pipeClassId, revisionId, 'branchtables').pipe(map(r => r.comments))
    });
  }

  /**
   * Create a new comment
   */
  /**
   * Create a new root comment using the dedicated single-create endpoint.
   * Using the autogenerated client ensures the request goes to the correct API base URL.
   * The BE sets authorId from the JWT and returns it in the response, so the FE can
   * rely on the returned object to show author-specific controls (edit/delete buttons).
   */
  createComment(request: CreateCommentRequest): Observable<CommentViewModel> {
    const body = new AutogenCreateCommentRequest();
    body.pipeClassId = request.pipeClassId;
    body.revisionId = parseInt(request.revisionId);
    body.contextType = request.contextType;
    body.parentCommentId = request.parentCommentId || undefined;
    body.content = request.content;
    body.authorName = request.authorName;
    body.displayOrder = 0;

    return this.esraApiClient.createComment(body).pipe(
      map(autogeneratedToFrontendComment),
      catchError((error) => {
        if (error?.status === 403) {
          console.error('Permission denied creating comment (403): user is not a participant in this revision.');
        } else {
          console.error('Error creating comment:', error);
        }
        return throwError(error);
      })
    );
  }

  /**
   * Update an existing comment using the dedicated PUT endpoint.
   * Only the author of the comment can call this — the BE enforces it and returns 403 otherwise.
   * Using the autogenerated client ensures the request goes to the correct API base URL.
   */
  updateComment(request: UpdateCommentRequest): Observable<CommentViewModel> {
    const body = new AutogenUpdateCommentRequest();
    body.id = request.id;
    body.content = request.content;
    body.resolutionStatus = request.resolutionStatus as unknown as AutogenCommentResolutionStatus;

    return this.esraApiClient.updateComment(request.id, body).pipe(
      map(autogeneratedToFrontendComment),
      catchError((error) => {
        if (error?.status === 403) {
          console.error('Permission denied editing comment (403): only the comment author can edit.');
        } else {
          console.error('Error updating comment:', error);
        }
        return throwError(error);
      })
    );
  }

  /**
   * Delete a comment.
   * The BE returns 202 Accepted with the deleted CommentViewModel in the body,
   * which the caller can use to confirm removal from local state.
   * Uses the autogenerated client so the request goes to the correct API base URL.
   */
  deleteComment(request: DeleteCommentRequest): Observable<CommentViewModel> {
    return this.esraApiClient.deleteComment(request.id)
      .pipe(map(autogeneratedToFrontendComment));
  }

  /**
   * MAIN OPERATION: Save all comments for a specific context (bulk save strategy).
   * Input: flat list with parentCommentId set on replies (root comments have null parentCommentId).
   * Output: nested list — the backend now returns replies embedded in each root comment.
   */
  saveCommentsForContext(pipeClassId: string, revisionId: string, contextType: 'summary' | 'components' | 'valves' | 'branchtables', comments: CommentViewModel[]): Observable<CommentViewModel[]> {
    // Flatten threaded frontend comments to send as a flat list to the backend.
    const flatComments = this.flattenComments(comments);

    // Filter out empty comments.
    const validComments = flatComments.filter(comment =>
      comment.content && comment.content.trim().length > 0
    );

    let displayOrder: number = 0;

    const backendComments: AutogeneratedCommentViewModel[] = validComments.map(comment => {
      const backendComment = new AutogeneratedCommentViewModel();

      // Only include id if it's a valid GUID (existing comments); new comments get undefined so the BE assigns one.
      if (comment.id && comment.id !== 'temp' && this.isValidGuid(comment.id)) {
        backendComment.id = comment.id;
      } else {
        backendComment.id = undefined;
      }

      backendComment.pipeClassId = pipeClassId;
      backendComment.revisionId = parseInt(revisionId);
      backendComment.contextType = contextType;

      // Only send parentCommentId when it is a real persisted GUID.
      if (comment.parentCommentId && this.isValidGuid(comment.parentCommentId)) {
        backendComment.parentCommentId = comment.parentCommentId;
      } else {
        backendComment.parentCommentId = undefined;
      }

      // authorId is no longer sent — the BE extracts it from the JWT.
      backendComment.authorName = comment.authorName || 'Unknown User';
      backendComment.content = comment.content;
      // Must include resolutionStatus so the BE does not reset it to Pending on every save.
      backendComment.resolutionStatus = comment.resolutionStatus as unknown as AutogenCommentResolutionStatus;
      backendComment.createdDate = comment.createdDate;
      backendComment.updatedDate = comment.updatedDate;
      backendComment.displayOrder = displayOrder++;

      return backendComment;
    });

    const saveRequest = new SaveCommentsRequest();
    saveRequest.pipeClassId = pipeClassId;
    saveRequest.revisionId = parseInt(revisionId);
    saveRequest.contextType = contextType;
    saveRequest.comments = backendComments;

    // The backend response is now nested: root comments contain their replies.
    // autogeneratedToFrontendComment handles this recursively, so no manual tree-building needed.
    return this.esraApiClient.saveComments(saveRequest).pipe(
      map((savedComments) => savedComments.map(c => autogeneratedToFrontendComment(c))),
      catchError((error) => {
        if (error?.status === 403) {
          console.error('Permission denied saving comments (403): user is not a participant in this revision.');
        } else {
          console.error('Error saving comments:', error);
        }
        return throwError(error);
      })
    );
  }

  /**
   * Posts a reply to an existing root comment using the dedicated reply endpoint.
   * The backend only allows replies one level deep — replying to a reply is not supported.
   * authorId is taken from the JWT by the BE; we only send authorName for display.
   */
  addReply(parentCommentId: string, authorName: string, content: string, displayOrder: number = 0): Observable<CommentViewModel> {
    const request = new AddReplyRequest();
    request.content = content;
    request.authorName = authorName;
    request.displayOrder = displayOrder;

    return this.esraApiClient.addReply(parentCommentId, request).pipe(
      map(autogeneratedToFrontendComment),
      catchError((error) => {
        if (error?.status === 403) {
          console.error('Permission denied adding reply (403): user is not a participant in this revision.');
        } else {
          console.error('Error adding reply:', error);
        }
        return throwError(error);
      })
    );
  }

  /**
   * Save all comments across all contexts (uses the bulk save strategy)
   */
  saveAllComments(request: SaveAllCommentsRequest): Observable<{
    summary: CommentViewModel[];
    components: CommentViewModel[];
    valves: CommentViewModel[];
    branchtables: CommentViewModel[];
  }> {
    return forkJoin({
      summary: this.saveCommentsForContext(request.pipeClassId, request.revisionId, 'summary', request.summaryComments),
      components: this.saveCommentsForContext(request.pipeClassId, request.revisionId, 'components', request.componentComments),
      valves: this.saveCommentsForContext(request.pipeClassId, request.revisionId, 'valves', request.valveComments),
      branchtables: this.saveCommentsForContext(request.pipeClassId, request.revisionId, 'branchtables', request.branchTableComments)
    });
  }

  /**
   * Gets comment count for a specific context
   */
  getCommentCount(pipeClassId: string, revisionId: string, contextType: 'summary' | 'components' | 'valves' | 'branchtables'): Observable<number> {
    const params = new HttpParams()
      .set('pipeClassId', pipeClassId)
      .set('revisionId', revisionId.toString())
      .set('contextType', contextType);

    return this.http.get<number>(`${this.baseUrl}/count`, { params });
  }

  /**
   * Gets comments statistics for a pipe class and revision (across all contexts)
   */
  getCommentsStats(pipeClassId: string, revisionId: string): Observable<{ totalComments: number; unreadComments: number }> {
    return forkJoin({
      summary: this.getCommentCount(pipeClassId, revisionId, 'summary'),
      components: this.getCommentCount(pipeClassId, revisionId, 'components'),
      valves: this.getCommentCount(pipeClassId, revisionId, 'valves'),
      branchtables: this.getCommentCount(pipeClassId, revisionId, 'branchtables')
    }).pipe(
      map(counts => ({
        totalComments: counts.summary + counts.components + counts.valves + counts.branchtables,
        unreadComments: 0 // Not implemented yet
      }))
    );
  }

  /**
   * Validate if a string is a valid GUID
   */
  private isValidGuid(guid: string): boolean {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(guid);
  }

  /**
   * Builds threaded comment structure from flat list
   */
  private buildCommentThreads(flatComments: CommentViewModel[]): CommentViewModel[] {

    const commentMap = new Map<string, CommentViewModel>();
    const rootComments: CommentViewModel[] = [];

    // First pass: Reset all threading properties and build the comment map
    flatComments.forEach(comment => {
      comment.replies = [];
      comment.level = 0;
      commentMap.set(comment.id, comment);
    });

    // Second pass: Build the threading structure
    // Sort comments by created date to ensure proper hierarchy
    const sortedComments = [...flatComments].sort((a, b) => {
      return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
    });

    // Create a set to track which comments are replies (have parents)
    const replyCommentIds = new Set<string>();

    // First pass: identify all reply comments
    sortedComments.forEach(comment => {
      if (comment.parentCommentId) {
        replyCommentIds.add(comment.id);
      }
    });

    // Second pass: build the threading structure
    sortedComments.forEach(comment => {
      if (comment.parentCommentId) {

        if (commentMap.has(comment.parentCommentId)) {
          const parent = commentMap.get(comment.parentCommentId)!;
          comment.level = parent.level + 1;
          parent.replies.push(comment);
        } else {
          rootComments.push(comment);
        }
      } else if (!replyCommentIds.has(comment.id)) {
        // Only add as root if it's not a reply to another comment
        rootComments.push(comment);
      }
    });

    return rootComments;
  }

  /**
   * Updates the resolution status of a comment (Pending / Fixed / WontFix).
   * The BE identifies the caller from the JWT — no authorId is required in the body.
   */
  updateCommentResolutionStatus(
    commentId: string,
    status: CommentResolutionStatus
  ): Observable<CommentViewModel> {
    const request = new UpdateCommentResolutionStatusRequest();
    // Both enums share the same numeric values (0/1/2); cast is safe.
    request.resolutionStatus = status as unknown as AutogenCommentResolutionStatus;

    return this.esraApiClient.updateCommentResolutionStatus(commentId, request).pipe(
      map(autogeneratedToFrontendComment),
      catchError((error) => {
        if (error?.status === 403) {
          console.error('Permission denied updating resolution status (403): user is not a participant in this revision.');
        } else {
          console.error('Error updating resolution status:', error);
        }
        return throwError(error);
      })
    );
  }

  /**
   * Returns true when at least one comment on the revision is still Pending,
   * meaning the revision cannot be published yet.
   * Used to gate the "Publish" button in the revisions dashboard.
   */
  hasPendingComments(revisionId: string): Observable<boolean> {
    return this.esraApiClient.hasPendingComments(parseInt(revisionId));
  }

  /**
   * Flattens threaded comments to a flat list for backend storage
   */
  private flattenComments(threadedComments: CommentViewModel[]): CommentViewModel[] {
    const flatComments: CommentViewModel[] = [];

    function traverse(comments: CommentViewModel[], level: number = 0) {
      comments.forEach(comment => {
        flatComments.push(comment);
        if (comment.replies && comment.replies.length > 0) {
          traverse(comment.replies, level + 1);
        }
      });
    }

    traverse(threadedComments);
    return flatComments;
  }
}
